import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Undo2, Eye, EyeOff } from 'lucide-react';

import NewAccountService from '../../services/admin-account-service';
import NewRoleService from '../../services/role-service';

import { useAuth } from '../../contexts/AuthContext';
import { showToast } from '../../components/ToastConfig';
import { STATUS_MESSAGES } from '../../utils/api-handlers/authentication-forms';

function AccountForm() {
  const Navigate = useNavigate();
  const { id } = useParams();
  const [mode, setMode] = useState('create');
  const [accountData, setAccountData] = useState(null);
  const [roleOptions, setRoleOptions] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  
  const { logout } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    reset
  } = useForm({
    mode: 'onChange'
  });

  // 獲取角色列表
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await NewRoleService.search();
        setRoleOptions(res.data?.data);
      } catch (error) {
        console.error('Failed to fetch roles:', error);
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          logout();
        } else {
          showToast.error('獲取角色列表失敗');
        }
      }
    };

    fetchRoles();
  }, []);

  // 獲取帳號資料
  useEffect(() => {
    const fetchAccountData = async () => {
      if (!id || id === "new") {
        setMode('create');
        return;
      }

      try {
        setMode('edit');
        const data = await NewAccountService.getDetails(id);
        setAccountData(data.data);
        
        // 設置表單預設值
        Object.keys(data.data).forEach(key => {
          setValue(key, data.data[key]);
        });
      } catch (error) {
        console.error('Failed to fetch account data:', error);
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          // showToast.error('無權訪問此頁面');
          // Navigate('/account/admin');
          // return;
          logout();
        } else if (error?.response?.status === 400 && error?.response?.data?.errorCode === '2000') {
          Navigate('/admin');
          return;
        } else {
          showToast.error('獲取帳號資料失敗');
        }
      }
    };

    fetchAccountData();
  }, [id, setValue, Navigate]);

  const onSubmit = async (data) => {
    // 如果有輸入密碼，檢查是否與確認密碼相符
    if (data.newPKey && data.newPKey !== data.newPKeyConfirm) {
      showToast.error('密碼與確認密碼不符');
      return;
    }

    // 如果是編輯模式且沒有輸入密碼，從提交數據中移除密碼相關欄位
    if (mode === 'edit' && !data.newPKey.trim()) {
      delete data.newPKey;
      delete data.newPKeyConfirm;
    }

    try {
      let res;
      if (mode === 'edit') {
        res = await NewAccountService.update(id, data);
        showToast.success('帳號更新成功');
      } else {
        res = await NewAccountService.create(data);
        showToast.success('帳號建立成功');
      }

      setTimeout(() => {
        Navigate('/admin');
      }, 1000);
    } catch (error) {
      console.error('Form submission failed:', error);
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        logout();
      } else if (error?.response?.status === 400) {
        showToast.error(STATUS_MESSAGES[error?.response?.data?.errorCode] || '提交失敗，請稍後再試');
      } else {
        showToast.error(error?.response?.data?.message || '提交失敗，請稍後再試');
      }
    }
  };

  return (
    <div className="container mx-auto p-6">
      <Link to="/admin" className="text-blue-500 hover:text-blue-600 group flex items-center mb-5 w-fit p-3 -ml-3">
        <Undo2 size={36} className="text-blue-500 group-hover:text-blue-600 mr-1" />  
        <span className="font-bold">返回</span>
      </Link>

      <div className="mx-auto bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold mb-6">
          {mode === 'edit' ? '編輯管理員帳號' : '新增管理員帳號'}
        </h1>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 姓名 */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                姓名
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                {...register('name', { 
                  required: '姓名為必填欄位'
                })}
                className={`w-full p-2 border rounded ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.name && (
                <p className="text-red-500 text-sm">{errors.name.message}</p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                Email
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                {...register('email')}
                disabled={mode === 'edit'}
                className={`w-full p-2 border rounded border-gray-300 ${mode === "edit" ? "bg-gray-50" : ""}`}
              />
            </div>

            {/* 職稱 */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                職稱
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                {...register('jobTitle', { 
                  required: '職稱為必填欄位'
                })}
                className={`w-full p-2 border rounded ${errors.jobTitle ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.jobTitle && (
                <p className="text-red-500 text-sm">{errors.jobTitle.message}</p>
              )}
            </div>

            {/* 角色 */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                角色
                <span className="text-red-500 ml-1">*</span>
              </label>
              <select
                {...register('role', { 
                  required: '角色為必填欄位'
                })}
                className={`w-full p-2 border rounded ${errors.role ? 'border-red-500' : 'border-gray-300'}`}
              >
                {/* <option value="">請選擇角色</option> */}
                {roleOptions.map((option, index) => (
                  <option key={option.name || index} value={option.name}>
                    {option.displayName}
                  </option>
                ))}
              </select>
              {errors.role && (
                <p className="text-red-500 text-sm">{errors.role.message}</p>
              )}
            </div>

            {/* 密碼 */}
            {mode === "edit" && (
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                密碼
                <span className="text-red-500 text-xs ml-2">
                  {mode === 'edit' ? '(若不修改請留空)' : ''}
                </span>
              </label>
              <div className="relative">
                <input 
                  type={showPassword ? "password" : "text"}
                  {...register('newPKey', {
                    pattern: {
                      value: /^.{6,}$/i,
                      message: '密碼至少需六位數'
                    }
                  })}
                  className="w-full p-2 border rounded border-gray-300"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.newPKey && (
                <p className="text-red-500 text-sm">{errors.newPKey.message}</p>
              )}
            </div>
            )}

            {/* 確認密碼 */}
            {mode === "edit" && (
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                確認密碼
                <span className="text-red-500 text-xs ml-2">
                  {mode === 'edit' ? '(若不修改請留空)' : ''}
                </span>
              </label>
              <div className="relative">
                <input 
                  type={showPassword ? "password" : "text"}
                  {...register('newPKeyConfirm', {
                    pattern: {
                      value: /^.{6,}$/i,
                      message: '密碼至少需六位數'
                    }
                  })}
                  className="w-full p-2 border rounded border-gray-300"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.newPKeyConfirm && (
                <p className="text-red-500 text-sm">{errors.newPKeyConfirm.message}</p>
              )}
            </div>
            )}

            {/* 建立時間 */}
            {mode === 'edit' && (
              <div className="space-y-1">
                <label className="block text-sm font-medium">建立時間</label>
                <input
                  {...register('createTime')}
                  disabled
                  className="w-full p-2 border rounded border-gray-300 bg-gray-50"
                />
              </div>
            )}

            {/* 登入時間 */}
            {mode === 'edit' && (
              <div className="space-y-1">
                <label className="block text-sm font-medium">最後登入時間</label>
                <input
                  {...register('loginTime')}
                  disabled
                  className="w-full p-2 border rounded border-gray-300 bg-gray-50"
                />
              </div>
            )}

            {/* 啟用狀態 */}
            <div className="space-y-1 col-span-2">
              <label className="block text-sm font-medium mb-2">
                啟用狀態
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setValue('isEnabled', !watch('isEnabled'))}
                  className={`relative inline-flex h-9 w-[4.5rem] items-center rounded-full transition-colors duration-300 
                    ${watch('isEnabled') ? 'bg-green-600 bg-opacity-20' : 'bg-gray-200'}
                    hover:bg-opacity-30
                  `}
                >
                  <span
                    className={`inline-block h-7 w-7 transform rounded-full transition-transform duration-300 ${
                      watch('isEnabled') ? 'translate-x-9 bg-green-600' : 'translate-x-1 bg-gray-400'
                    }`}
                  />
                  <span 
                    className={`absolute ${
                      watch('isEnabled')
                        ? 'left-2.5 text-green-600'
                        : 'right-2.5 text-gray-500'
                    }`}
                  >
                    {watch('isEnabled') ? '是' : '否'}
                  </span>
                </button>
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-8">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              disabled={isSubmitting}
            >
              {isSubmitting ? "提交中..." : "提交"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AccountForm;